import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container } from '@mui/material';
import { trackScreenView } from '../lib/gtag';

export default function BBFTheSnare() {
  const sectionRef = useRef();
  const hasTrackedRef = useRef(false);
  const wasJustJumped = useRef(false);

  useEffect(() => {
    let hasFired = false;
    let animationFrameId;
    let timeoutId;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasFired && !window.__skipNextTracking) {
        trackScreenView('BBFTheSnare');
        hasFired = true;
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.3 });

    const node = sectionRef.current;
    if (node) {
      timeoutId = setTimeout(() => {
        animationFrameId = requestAnimationFrame(() => observer.observe(node));
      }, 100);
    }

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    const detailsEls = document.querySelectorAll("details");

    detailsEls.forEach(details => {
      const summary = details.querySelector("summary");

      // ✅ Animate on first scroll into view
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          summary.classList.add("scroll-bounce");
          setTimeout(() => {
            summary.classList.remove("scroll-bounce");
          }, 1200); // duration of bounce
          observer.disconnect(); // only fire once
        }
      }, { threshold: 0.6 }); // Customize as needed

      if (summary) observer.observe(summary);

      // ✅ Animate shake on close
      details.addEventListener("toggle", () => {
        summary.classList.remove("animate-shake");

        if (!details.open) {
          void summary.offsetWidth;
          summary.classList.add("animate-shake");
        }
      });
    });
  }, []);
  
  return (
    <Box ref={sectionRef} component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fdf6e3', color: '#111' }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h2" gutterBottom>The Snare</Typography>

        <Typography variant="body1" paragraph>
          Butler wasn’t just a location. It was a setup.
        </Typography>

        <Typography variant="body1" paragraph>
          Just nine miles from the Big Butler Fairgrounds where Donald Trump was shot, a 21-year-old George Washington was nearly assassinated during a scouting expedition in what is now Butler County, Pennsylvania. The year was 1753 — two decades before the American Revolution.
        </Typography>

        <Typography variant="body1" paragraph>
          It raises the question:
        </Typography>

        <Typography variant="body1" paragraph>
          Would the American Revolution have succeeded if Washington had died in Butler?
        </Typography>

        <Typography variant="body1" paragraph>
          That’s the power of the place. Butler is not just geography. It’s legacy. A place where fate turned — and the United States of America nearly fell apart.
        </Typography>

        <Typography variant="body1" paragraph>
          And then there’s Joe Biden — who didn’t just stumble into the symbolism. He leaned into it.
        </Typography>

        <Typography variant="body1" paragraph>
          In 2018, Biden mocked Donald Trump with a carefully scripted line he repeated throughout the 2020 campaign:
        </Typography>

        <Typography variant="h6" sx={{ fontStyle: 'italic', mt: 2, mb: 2 }}>
          “President Trump is more like George Wallace than George Washington.”
        </Typography>

        <details style={{ marginBottom: '1.5rem' }}>
          <summary style={{ color: 'red', cursor: 'default', fontWeight: 'bold' }}>
            WHY THAT COMPARISON WAS MORE THAN RHETORIC
          </summary>
          <Box sx={{ pl: 2, mt: 2, borderLeft: '4px solid #ccc' }}>
            <Typography variant="body1" paragraph>
              It was meant as an insult — casting Trump as divisive, segregationist, and unworthy of the office. But in retrospect, it reads like something far darker.
            </Typography>
            <Typography variant="body1" paragraph>
              Because here’s what the Pennsylvanian, Joe Biden knew:
            </Typography>
            <ul style={{ paddingLeft: '1.5rem', marginTop: 0, marginBottom: '1.5rem' }}>
              <li><Typography variant="body1" paragraph>George Wallace was nearly assassinated while campaigning.</Typography></li>
              <li><Typography variant="body1" paragraph>George Washington was nearly assassinated in Butler County.</Typography></li>
              <li><Typography variant="body1" paragraph><strong>And Donald Trump?</strong></Typography></li>
            </ul>
          </Box>
        </details>

        <Typography variant="body1" paragraph>
          Biden again sent a signal:
        </Typography>

        <Typography variant="h6" sx={{ fontStyle: 'italic', mt: 2, mb: 2 }}>
          “Put Trump in a bullseye.”
        </Typography>

        <Typography variant="body2" paragraph>— Joe Biden, July 8, 2024</Typography>

        <Typography variant="body1" paragraph>
          Five days later, Trump was shot — in Butler. The Butler Bullseye.
        </Typography>

        <Typography variant="body1" paragraph>
          This wasn’t metaphor. It was choreography.
        </Typography>

        <Typography variant="body1" paragraph>
          A decades-long buildup. A corridor of power. A trap disguised as coincidence. A lone gunman, quickly cremated.
        </Typography>

        <Typography variant="body1" paragraph>
          The Rosemont Seneca Corridor was real — and Butler sat Dead Center.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>
          The Butler Bullseye.
        </Typography>

        <Typography variant="body1" paragraph>
          The snare was set. Trump walked into it — and was shot.
        </Typography>

        <Typography variant="body1" paragraph>
          The bullseye was no accident. It was the kill zone.
        </Typography>
      </Container>
    </Box>
  );
}